'use client';

import { IBlogArticleListItem } from '@/api/types/blog';
import InfiniteScroll from '@/components/InfiniteScroll';
import Pagination from '@/components/Pagination';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { scrollToAnchor } from '@/utils/tools';
import React, { useContext, useEffect, useState } from 'react';
import ArticleCard from './ArticleCard';
import BlogNoArticle from './BlogNoArticle';

interface ArticleListProps {
  title?: string;
  articles: IBlogArticleListItem[];
  page: number;
  total: number;
  loading?: boolean;
  listExp?: (article: IBlogArticleListItem, index: number) => void;
  listItemClick?: (
    article: IBlogArticleListItem,
    index: number,
    clickArea: 'article' | 'author',
  ) => void;
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
  titleComponent?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  className?: string;
}

const ArticleList: React.FC<ArticleListProps> = ({
  title = '',
  articles,
  page,
  total,
  loading = false,
  listExp = () => {},
  listItemClick = () => {},
  onPageChange,
  onLoadMore,
  titleComponent = 'h4',
  className = '',
}) => {
  const [limit] = useState<number>(18);
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;

  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);

  // 处理文章曝光
  const handleArticleExp = (article: IBlogArticleListItem, index: number) => {
    listExp(article, index);
  };

  // 处理文章点击
  const handleArticleClick = (article: IBlogArticleListItem, index: number) => {
    listItemClick(article, index, 'article');
  };

  // 处理作者点击
  const handleAuthorClick = (article: IBlogArticleListItem, index: number) => {
    listItemClick(article, index, 'author');
  };

  // 处理滚动到底部
  const handleReachBottom = () => {
    setIsShowFooterNav(false);
    onLoadMore?.();
  };

  // 处理分页变化
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    logger.log('handlePageChange', value);
    // 状态更新后延迟执行滚动，确保DOM更新完成
    setTimeout(() => {
      scrollToAnchor('#article-list', isWeb ? 100 : 45);
    }, 0);
    onPageChange?.(value);
  };

  useEffect(() => {
    if (isWeb) return;
    if (Array.isArray(articles) && articles.length >= total) {
      setIsShowFooterNav(true);
    } else {
      setIsShowFooterNav(false);
    }
  }, [articles, total]);

  // 动态标题组件
  const TitleComponent = titleComponent;
  return (
    <div
      className={`
      latest-news w-full  min-h-[95vh]
      ${deviceType === EPlatform.WEB ? 'pt-[5.42vw]' : 'px-14 pt-30'}
      ${className}
    `}
      id="article-list"
    >
      {/* 标题 */}
      {title && (
        <div className="title">
          <TitleComponent
            className={`title-text font-bb ${deviceType === EPlatform.WEB ? 'text-32' : 'text-20'}`}
          >
            {title}
          </TitleComponent>
        </div>
      )}

      {/* 文章列表 */}
      {Array.isArray(articles) && articles.length > 0 && (
        <ul
          className={`
            news-list
            ${deviceType === EPlatform.WEB ? 'mt-[1.67vw] grid grid-cols-3 gap-x-[24px] gap-y-[64px] [@media(max-width:1540px)]:grid-cols-2 [@media(max-width:960px)]:grid-cols-1' : 'mt-16'}
          `}
        >
          {articles.map((article, index) => (
            <li
              key={article.id}
              className={`${deviceType === EPlatform.WEB ? '' : 'not-first:mt-[80px]'}`}
            >
              <ArticleCard
                article={article}
                showIntro
                onArticleExp={() => handleArticleExp(article, index)}
                onArticleClick={() => handleArticleClick(article, index)}
                onAuthorClick={() => handleAuthorClick(article, index)}
              />
            </li>
          ))}
        </ul>
      )}
      {Array.isArray(articles) && articles.length === 0 && !loading && <BlogNoArticle />}
      {Array.isArray(articles) && articles.length === 0 && loading && <div className="h-[60px]" />}
      <WapOnly>
        <InfiniteScroll
          onReachBottom={handleReachBottom}
          loading={loading}
          page={page}
          total={total}
          limit={limit}
        />
      </WapOnly>
      <WebOnly>
        {total > limit && (
          <Pagination
            current={page}
            total={total}
            pageSize={limit}
            onChange={handlePageChange}
            className="mt-[2.6vw] pb-[64px]"
            sx={{
              '& .Mui-disabled': {
                color: '#28282822 !important',
                backgroundColor: 'transparent',
              },
            }}
          />
        )}
      </WebOnly>
    </div>
  );
};

export default ArticleList;
